import { enumSchema } from '@shared/utils/enum.utils';

import { ImportStatus } from '../enums/import-status.enum';
import { LabourCostMethod } from '../enums/labour-cost-method.enum';
import { ProductTemplateProviderType } from '../enums/product-template-provider-type.enum';
import { getMoneySchema, Money } from '../utils/money.model';

export interface ProductTemplate {
  id: string;
  created?: Date;

  projectId: string;
  sequence: number;
  provider: ProductTemplateProviderType;

  name: string;
  referenceCode: string;
  type: string;
  unitOfMeasure: string;
  notes: string;

  skillsRequired: string;
  specialTools: string;
  supplier: string;
  supplierCode: string;
  unionCode?: string;
  unionName?: string;

  labourCostMethod: LabourCostMethod;
  baseSalaryPerUnit: Money; // base salary per per unit. Only valid when labourCostMethod = labourCosts
  baseUnitTime: number; // base hours required to install an unit. Only valid when labourCostMethod = installationTime

  unitCost: Money;
  additionalCostsPercentage?: number;
  additionalSubcontractorCosts: Money;
  specialTreatmentCosts: Money;
  additionalLabourCostsPercentage?: number;
  quantityDocumentation: number;
  unknownColumns?: Record<string, string>; // INFO (Denis) - this field is only present when parsing products from an imported file.

  reminders: ProductTemplateReminder[];
}

export interface ExternalProductTemplate {
  id: string;
  provider: ProductTemplateProviderType;
  name: string;
  referenceCode: string;
  type: string;
  unitOfMeasure: string;
  notes: string;
  categoryCode: string;
  supplier?: string;
  supplierCode?: string;
  standardItemIds?: string;
  unitCost?: Money;
  discount?: number;
  isNetPrice: boolean;
}

export interface ProductTemplateReminder {
  id: string;
  name: string;
  weeksOrderPeriod: number;
  description: string;
}

export type InteractiveProductImportRow = ProductTemplate & {
  [key: string]: unknown;
  importId: string;
  importStatus: ImportStatus;
  importAsTemplate?: boolean;
  linkedProductId?: string;
  phaseId?: string;
  activityId?: string;
  linkedProductLabel?: string;
  phaseName?: string;
  activityName?: string;
  quantityByConstructionAreaId: Record<string, number>;
  rowError?: string;
  rowStatusLabel?: string;
  selected?: boolean;
  isProcessed?: boolean;
};

export const productTemplateChatbotFields = {
  id: { type: 'string', description: 'Must be specified for update or delete.' },
  name: {
    type: 'string',
    description: 'Must be specified for new entries. Otherwise should be only set if the intend it to change it.',
  },
  order: {
    type: 'number',
    description: 'Must be specified for new entries. Otherwise should be only set if the intend it to change it.',
  },
  referenceCode: {
    type: 'string',
    description: 'Must be specified for new entries. Otherwise should be only set if the intend it to change it.',
  },
  type: { type: 'string', description: 'Optional. Should be only set if the intend is to change it.' },
  unitOfMeasure: { type: 'string', description: 'Optional. Should be only set if the intend is to change it.' },
  notes: { type: 'string', description: 'Optional. Should be only set if the intend is to change it.' },
  skillsRequired: { type: 'string', description: 'Optional. Should be only set if the intend is to change it.' },
  specialTools: { type: 'string', description: 'Optional. Should be only set if the intend is to change it.' },
  supplier: { type: 'string', description: 'Optional. Should be only set if the intend is to change it.' },
  supplierCode: { type: 'string', description: 'Optional. Should be only set if the intend is to change it.' },
  labourCostMethod: enumSchema('LabourCostMethod', LabourCostMethod),
  baseSalaryPerUnit: getMoneySchema(
    'Must be set when the LabourCostMethod is UnionPrice. When updating should be only set if the intend is to change it.'
  ),
  baseUnitTime: {
    type: 'number',
    description:
      'Must be set when the LabourCostMethod is installationTime. When updating should be only set if the intend is to change it.',
  },
  unitCost: getMoneySchema('Describes the cost of purchasing an items.'),
  additionalCostsPercentage: {
    type: 'number',
    description: 'Optional. Should be only set if the intend is to change it.',
  },
  additionalSubcontractorCosts: getMoneySchema('Optional.'),
  specialTreatmentCosts: getMoneySchema('Optional'),
  additionalLabourCostsPercentage: {
    type: 'number',
    description: 'Optional. Should be only set if the intend is to change it.',
  },
  quantityDocumentation: { type: 'number', description: 'Optional. Should be only set if the intend is to change it.' },
};

export const interactiveProductImportChatbotFields = {
  importId: { type: 'string', description: 'Imported row id to update.' },
  importStatus: enumSchema(
    'ImportStatus',
    ImportStatus,
    'Use new for rows that create products and update for rows that modify linked products.'
  ),
  linkedProductId: {
    type: 'string',
    description: 'Existing product id. Required when importStatus is update.',
  },
  phaseId: { type: 'string', description: 'Target phase id.' },
  activityId: { type: 'string', description: 'Target activity id.' },
  quantityByConstructionAreaId: {
    type: 'object',
    additionalProperties: { type: 'number' },
    description: 'Map of constructionAreaId to quantity.',
  },
  unknownColumns: {
    type: 'object',
    additionalProperties: { type: 'string' },
    description: 'Map of unknown imported Excel column name to value.',
  },
  ...productTemplateChatbotFields,
};
